import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableRow,
  LinearProgress,
  FormControlLabel,
  Switch,
  IconButton,
  Tooltip
} from '@mui/material';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import api from '../api/axiosConfig';
import { getMediaBaseUrl } from '../utils/mediaUrl';
import { usePositions } from '../utils/positions';

const BOOST_OPTIONS = [
  { key: 'points', labelKey: 'estadisticasTab.pts' },
  { key: 'minutes', labelKey: 'estadisticasTab.min' },
  { key: 'assists', labelKey: 'estadisticasTab.ast' },
  { key: 'rebounds_off', labelKey: 'estadisticasTab.rebO' },
  { key: 'rebounds_def', labelKey: 'estadisticasTab.rebD' },
  { key: 'total_rebounds', labelKey: 'estadisticasTab.rebT' },
  { key: 'steals', labelKey: 'estadisticasTab.stl' },
  { key: 'blocks', labelKey: 'estadisticasTab.blk' },
  { key: 'turnovers', labelKey: 'estadisticasTab.to' },
  { key: 'two_fg_percent', labelKey: 'estadisticasTab.t2' },
  { key: 'three_fg_percent', labelKey: 'estadisticasTab.t3' },
  { key: 'free_throw_percent', labelKey: 'estadisticasTab.tl' },
  { key: 'ranking', labelKey: 'estadisticasTab.val' },
  { key: 'avg_two_att', labelKey: 'estadisticasTab.volT2' },
  { key: 'avg_three_att', labelKey: 'estadisticasTab.volT3' },
  { key: 'avg_ft_att', labelKey: 'estadisticasTab.volTL' }
];

const SimilarStatsPlayersTab = ({ playerId }) => {
  const { t } = useTranslation();
  const { translateFromDB } = usePositions(t);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filterPosition, setFilterPosition] = useState(true);
  const [boostStats, setBoostStats] = useState([]);

  const toggleBoost = (key) => {
    setBoostStats(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  useEffect(() => {
    if (!playerId) return;
    setLoading(true);
    setError(null);

    const params = {
      filterPosition: filterPosition.toString()
    };
    if (boostStats.length > 0) {
      params.boostStats = boostStats.join(',');
    }

    api.get(`/api/player-stats/${playerId}/similar-stats`, { params })
      .then(res => setData(res.data))
      .catch(() => setError(t('estadisticasTab.similarStats.error')))
      .finally(() => setLoading(false));
  }, [playerId, filterPosition, boostStats, t]);

  const boostChips = (
    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
      <Typography variant="body2" color="text.secondary" sx={{ mr: 0.5, alignSelf: 'center', fontWeight: 500 }}>
        {t('estadisticasTab.similarStats.boost')}
      </Typography>
      {BOOST_OPTIONS.map(opt => (
        <Chip
          key={opt.key}
          label={t(opt.labelKey)}
          size="small"
          variant={boostStats.includes(opt.key) ? 'filled' : 'outlined'}
          color={boostStats.includes(opt.key) ? 'primary' : 'default'}
          onClick={() => toggleBoost(opt.key)}
          sx={{ fontSize: '0.7rem', height: 24, cursor: 'pointer' }}
        />
      ))}
    </Box>
  );

  if (loading) {
    return (
      <Box sx={{ p: { xs: 1, md: 2 } }}>
        {boostChips}
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: { xs: 1, md: 2 } }}>
        {boostChips}
        <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>
      </Box>
    );
  }

  if (!data || !data.similarPlayers || data.similarPlayers.length === 0) {
    return (
      <Box>
        <Box sx={{ p: { xs: 1, md: 2 }, mb: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <FormControlLabel
            control={
              <Switch
                checked={filterPosition}
                onChange={(e) => setFilterPosition(e.target.checked)}
                size="small"
              />
            }
            label={<Typography variant="body2">{t('estadisticasTab.similarStats.samePositionOnly')}</Typography>}
          />
          {boostChips}
        </Box>
        <Card sx={{ p: 4, textAlign: 'center', bgcolor: 'grey.50', m: 2 }}>
          <Typography variant="h6" color="text.secondary">
            {t('estadisticasTab.similarStats.noResults')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t('estadisticasTab.similarStats.noResultsHint')}
          </Typography>
        </Card>
      </Box>
    );
  }

  const getSimilarityColor = (pct) => {
    if (pct >= 85) return '#4CAF50';
    if (pct >= 70) return '#8BC34A';
    if (pct >= 55) return '#FF9800';
    return '#F44336';
  };

  const getDiffColor = (diff) => {
    if (diff > 0) return 'success.main';
    if (diff < 0) return 'error.main';
    return 'text.secondary';
  };

  const formatDiff = (diff, unit) => {
    const sign = diff > 0 ? '+' : '';
    return `${sign}${diff}${unit ? ' ' + unit : ''}`;
  };

  const formatMetricValue = (metric, isTarget) => {
    const val = isTarget ? metric.targetValue : metric.playerValue;
    const made = isTarget ? metric.targetMade : metric.playerMade;
    const totalAtt = isTarget ? metric.targetTotalAtt : metric.playerTotalAtt;
    const att = isTarget ? metric.targetAttempts : metric.playerAttempts;
    const base = `${val}${metric.unit ? ` ${metric.unit}` : ''}`;
    if (totalAtt !== undefined) return `${base} (${made}/${totalAtt})`;
    if (att !== undefined) return `${base} (${att} att.)`;
    return base;
  };

  // Build projection lookup per player: metric.key → { projectedValue, projectedDiff, factor }
  const getProjectionByKey = (player) => {
    if (!player.projection?.metrics) return null;
    const map = {};
    player.projection.metrics.forEach(pm => { map[pm.key] = pm; });
    return map;
  };

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Chip
              label={`${data.similarPlayers.length} ${t('estadisticasTab.similarStats.playersFound')}`}
              size="small"
              color="primary"
              variant="outlined"
            />
            {data.targetPlayer?.mainCompetitionEb && (
              <Chip
                label={`${t('estadisticasTab.similarStats.origin')}: ${data.targetPlayer.mainCompetitionEb}`}
                size="small" variant="outlined" color="info"
              />
            )}
            <Tooltip
              title={t('estadisticasTab.similarStats.howItWorks')}
              arrow
              placement="bottom-start"
              slotProps={{ tooltip: { sx: { maxWidth: 350, fontSize: '0.8rem', lineHeight: 1.5 } } }}
            >
              <IconButton size="small" sx={{ p: 0.5 }}>
                <HelpOutlineIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              </IconButton>
            </Tooltip>
          </Box>
          <FormControlLabel
            control={
              <Switch
                checked={filterPosition}
                onChange={(e) => setFilterPosition(e.target.checked)}
                size="small"
              />
            }
            label={
              <Typography variant="body2">
                {t('estadisticasTab.similarStats.samePositionOnly')}
              </Typography>
            }
          />
        </Box>
        {boostChips}
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {data.similarPlayers.map((player) => {
          const simColor = getSimilarityColor(player.similarity);
          const avatarSrc = `${getMediaBaseUrl()}/photos/${player.playerId}.png?${Date.now()}`;
          const hasProjection = !!player.projection;
          const projByKey = getProjectionByKey(player);

          return (
            <Card
              key={player.playerId}
              sx={{
                overflow: 'visible',
                border: '1px solid',
                borderColor: 'divider',
                '&:hover': { boxShadow: 4 },
                transition: 'box-shadow 0.2s'
              }}
            >
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                {/* Header */}
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 1.5
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar
                      src={avatarSrc}
                      alt={player.playerName}
                      sx={{ width: 44, height: 44 }}
                    >
                      {player.playerName?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                    </Avatar>
                    <Box>
                      <Link
                        to={`/players/${player.playerId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ textDecoration: 'none', color: 'inherit' }}
                      >
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 600,
                            cursor: 'pointer',
                            '&:hover': { textDecoration: 'underline' }
                          }}
                        >
                          {player.playerName}
                        </Typography>
                      </Link>
                      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
                        <Chip
                          label={translateFromDB(player.position)}
                          size="small"
                          sx={{ fontSize: '0.7rem', height: 20 }}
                        />
                        {player.age != null && (
                          <Chip
                            label={`${player.age} ${t('measurements.comparative.years')}`}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.65rem', height: 20 }}
                          />
                        )}
                        {player.games != null && (
                          <Chip
                            label={t('estadisticasTab.similarStats.games', { count: player.games })}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.65rem', height: 20 }}
                          />
                        )}
                        {hasProjection && (
                          <Chip
                            label={player.mainCompetition}
                            size="small"
                            variant="outlined"
                            color="warning"
                            sx={{ fontSize: '0.6rem', height: 18 }}
                          />
                        )}
                      </Box>
                    </Box>
                  </Box>

                  {/* Similarity badge */}
                  <Box sx={{ textAlign: 'center', minWidth: 60 }}>
                    <Typography
                      variant="h5"
                      sx={{ fontWeight: 700, color: simColor, lineHeight: 1 }}
                    >
                      {player.similarity}<Typography component="span" variant="body2" sx={{ color: simColor }}>%</Typography>
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={player.similarity}
                      sx={{
                        mt: 0.5,
                        height: 4,
                        borderRadius: 2,
                        bgcolor: 'grey.200',
                        '& .MuiLinearProgress-bar': {
                          bgcolor: simColor,
                          borderRadius: 2
                        }
                      }}
                    />
                  </Box>
                </Box>

                <Divider sx={{ mb: 1 }} />

                {/* Metrics table */}
                <Table size="small">
                  <TableBody>
                    {/* Header row */}
                    <TableRow sx={{ '& td': { border: 0, py: 0.5 } }}>
                      <TableCell sx={{ fontWeight: 600, color: 'text.secondary', width: hasProjection ? '20%' : '30%' }} />
                      <TableCell
                        align="center"
                        sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase' }}
                      >
                        {data.targetPlayer?.name?.split(' ').pop() || t('estadisticasTab.similarStats.you')}
                        {(data.targetPlayer?.age != null || data.targetPlayer?.games != null) && (
                          <Typography component="span" sx={{ fontSize: '0.6rem', fontWeight: 400, ml: 0.5, textTransform: 'none' }}>
                            ({[data.targetPlayer?.age != null ? `${data.targetPlayer.age}a` : null, data.targetPlayer?.games != null ? `${data.targetPlayer.games}m` : null].filter(Boolean).join(', ')})
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center" sx={{ width: 40 }} />
                      <TableCell
                        align="center"
                        sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase' }}
                      >
                        {player.playerName.split(' ').pop()}
                        {(player.age != null || player.games != null) && (
                          <Typography component="span" sx={{ fontSize: '0.6rem', fontWeight: 400, ml: 0.5, textTransform: 'none' }}>
                            ({[player.age != null ? `${player.age}a` : null, player.games != null ? `${player.games}m` : null].filter(Boolean).join(', ')})
                          </Typography>
                        )}
                      </TableCell>
                      {hasProjection && (
                        <TableCell
                          align="center"
                          sx={{ fontWeight: 700, color: '#F25F29', fontSize: '0.7rem', textTransform: 'uppercase' }}
                        >
                          {t('estadisticasTab.similarStats.projectedCol')}
                        </TableCell>
                      )}
                      <TableCell align="right" sx={{ width: '15%' }} />
                    </TableRow>

                    {(() => {
                      const percentToVolume = {
                        'two_fg_percent': 'avg_two_att',
                        'three_fg_percent': 'avg_three_att',
                        'free_throw_percent': 'avg_ft_att'
                      };
                      const volumeKeys = new Set(Object.values(percentToVolume));
                      const byKey = {};
                      player.metrics.forEach(m => { byKey[m.key] = m; });
                      const ordered = [];
                      player.metrics.forEach(m => {
                        if (volumeKeys.has(m.key)) return;
                        if (percentToVolume[m.key] && byKey[percentToVolume[m.key]]) {
                          ordered.push(byKey[percentToVolume[m.key]]);
                        }
                        ordered.push(m);
                      });
                      return ordered;
                    })().map((metric) => {
                      const proj = projByKey?.[metric.key];
                      const effectiveDiff = proj?.projectedDiff != null ? proj.projectedDiff : metric.diff;

                      return (
                        <TableRow
                          key={metric.key}
                          sx={{
                            '& td': { border: 0, py: metric.isVolume ? 0.3 : 0.75 },
                            '&:hover': { bgcolor: 'action.hover' },
                            bgcolor: boostStats.includes(metric.key) ? 'primary.50' : metric.isVolume ? '#f5f5f5' : 'inherit'
                          }}
                        >
                          <TableCell>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: boostStats.includes(metric.key) ? 700 : 400, fontStyle: metric.isVolume ? 'italic' : 'normal', fontSize: metric.isVolume ? '0.75rem' : 'inherit' }}>
                              {t(metric.label)}
                              {boostStats.includes(metric.key) && ' x2'}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'error.main' }}>
                              {formatMetricValue(metric, true)}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            {hasProjection && proj?.projectedValue != null ? (
                              <TrendingFlatIcon sx={{ fontSize: 16, color: '#F25F29' }} />
                            ) : (
                              <CompareArrowsIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {formatMetricValue(metric, false)}
                            </Typography>
                          </TableCell>
                          {hasProjection && (
                            <TableCell align="center">
                              {proj?.projectedValue != null ? (
                                <Tooltip title={`${proj.factor > 0 ? '+' : ''}${proj.factor}% (${player.projection.from} → ${player.projection.to})`}>
                                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#F25F29', fontStyle: 'italic' }}>
                                    {proj.projectedValue}{metric.unit ? ` ${metric.unit}` : ''}
                                  </Typography>
                                </Tooltip>
                              ) : (
                                <Typography variant="body2" color="text.disabled">—</Typography>
                              )}
                            </TableCell>
                          )}
                          <TableCell align="right">
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 500, color: getDiffColor(effectiveDiff) }}
                            >
                              {formatDiff(effectiveDiff, metric.unit)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>

                {/* Projection basis info */}
                {hasProjection && (
                  <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5, display: 'block', textAlign: 'right' }}>
                    {t('estadisticasTab.similarStats.projectionBasis', {
                      from: player.projection.from,
                      to: player.projection.to,
                      players: player.projection.unique_players
                    })}
                  </Typography>
                )}
              </CardContent>
            </Card>
          );
        })}
      </Box>
    </Box>
  );
};

export default SimilarStatsPlayersTab;
